import { useEffect, useState, type ReactNode } from 'react';
import { AppLoadingSplash } from '@eureca/ui';
import { setAccessTokenProvider, setUnauthorizedHandler } from '@eureca/admin-api-sdk/client';
import { ensureKeycloakInitialized, getKeycloak } from './keycloak-instance';

type GateState = 'loading' | 'ready' | 'error';

// N queries em paralelo que falhem `updateToken(30)` chamariam `login()` N vezes
// (reload-loop). Guarda de re-entrância única.
let loginInFlight = false;

function triggerKeycloakLogin(keycloak: ReturnType<typeof getKeycloak>): void {
  if (loginInFlight) return;
  loginInFlight = true;
  keycloak.login({ redirectUri: window.location.origin + '/' });
}

/**
 * Gate de autenticação do admin SPA. Inicializa o Keycloak (realm `admin`,
 * `login-required` por padrão), liga o provider de token + handler de 401 do
 * SDK, e renderiza os filhos só depois de autenticado.
 */
export function KeycloakGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const authenticated = await ensureKeycloakInitialized();
        if (cancelled) return;

        const keycloak = getKeycloak();

        if (!authenticated) {
          // Com `login-required` o KC já teria redirecionado; fallback defensivo.
          triggerKeycloakLogin(keycloak);
          return;
        }

        setAccessTokenProvider(async () => {
          try {
            await keycloak.updateToken(30);
            return keycloak.token ?? undefined;
          } catch {
            triggerKeycloakLogin(keycloak);
            return undefined;
          }
        });

        setUnauthorizedHandler(() => {
          triggerKeycloakLogin(keycloak);
        });

        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setErrorMessage(e instanceof Error ? e.message : 'Keycloak init failed');
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
      setAccessTokenProvider(null);
      setUnauthorizedHandler(null);
      loginInFlight = false;
    };
  }, []);

  if (state === 'error') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-muted px-4">
        <div className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
          <p className="text-sm text-muted-foreground">
            Não foi possível inicializar a autenticação.
            {errorMessage ? ` (${errorMessage})` : ''}
          </p>
          <button
            type="button"
            onClick={() => {
              try {
                window.localStorage.clear();
              } catch {
                // ignore
              }
              try {
                window.sessionStorage.clear();
              } catch {
                // ignore
              }
              loginInFlight = false;
              const keycloak = getKeycloak();
              try {
                keycloak.logout({ redirectUri: window.location.origin + '/' });
              } catch {
                window.location.href = window.location.origin + '/';
              }
            }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (state === 'loading') {
    return <AppLoadingSplash label="Entrando" />;
  }

  return <>{children}</>;
}
